/**
 * Turns an axios failure into something displayable.
 *
 * The backend has three distinct error body shapes, all of them reachable from
 * the auth endpoints:
 *   - `{error: string}`            — e.g. login's 401 (auth/routes.ts:211-213)
 *   - `{errors: {field: string[]}}` — DRF-style validation, e.g. register's
 *                                     duplicate-email 400 (auth/routes.ts:169-178)
 *   - `{detail | message: string}`  — the generic errorHandler fallbacks
 */
import { AxiosError } from 'axios';
import type { ApiErrorBody } from '../api/types';

export interface ParsedApiError {
  message: string;
  /** Per-field messages, when the backend sent DRF-style validation errors. */
  fieldErrors: Record<string, string[]>;
}

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

export function parseApiError(error: unknown): ParsedApiError {
  if (!(error instanceof AxiosError)) {
    return { message: FALLBACK_MESSAGE, fieldErrors: {} };
  }

  // No response at all: DNS failure, connection refused, CORS, or the 60s
  // timeout. Worth distinguishing — "check your connection" is actionable in a
  // way that a generic failure message is not.
  if (error.response === undefined) {
    return {
      message: 'Could not reach the server. Check your connection and try again.',
      fieldErrors: {},
    };
  }

  const body = error.response.data as ApiErrorBody | undefined;
  const fieldErrors = body?.errors ?? {};

  const message =
    body?.error ??
    body?.detail ??
    body?.message ??
    firstFieldMessage(fieldErrors) ??
    FALLBACK_MESSAGE;

  return { message, fieldErrors };
}

function firstFieldMessage(fieldErrors: Record<string, string[]>): string | undefined {
  for (const messages of Object.values(fieldErrors)) {
    const first = messages[0];
    if (first !== undefined) return first;
  }
  return undefined;
}
